import { ChangeDetectorRef, Component, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCardModule } from '@angular/material/card';
import { CommonModule } from '@angular/common';
import {
  TenantSettingApikeyComponent,
  WebhookViewModel,
} from './tenant-setting-apikey/tenant-setting-apikey.component';
import { TenantSettingTagComponent } from './tenant-setting-tag/tenant-setting-tag.component';
import {
  TenantAlertViewModel,
  TenantSettingNotificationComponent,
} from './tenant-setting-notification/tenant-setting-notification.component';
import { TagViewModel } from './tenant-setting-tag/tenant-setting-tag.component';

import { firstValueFrom } from 'rxjs';
import { TenantSettingService } from '@services/api/tenant-setting.service';
import { MatDialog } from '@angular/material/dialog';
import {
  DialogOption,
  TenantSettingTagEditDialogComponent,
} from './tenant-setting-tag-edit-dialog/tenant-setting-tag-edit-dialog.component';
import {
  TagAddRequest,
  TagUpdateListRequest,
  TagUpdateRequest,
  TenantAttrRequest,
} from '@entities/tenantSetting';
import {
  AlertDialogComponent,
  DialogOption as AlertDialogOption,
} from '@components/shared/alert-dialog/alert-dialog.component';
import {
  TenantSettingApikeyEditDialogComponent,
  TenantSettingApikeyViewModel,
} from './tenant-setting-apikey/tenant-setting-apikey-edit-dialog/tenant-setting-apikey-edit-dialog.component';
import { SelectionModel } from '@angular/cdk/collections';
import { TenantSettingBackupComponent } from './tenant-setting-backup/tenant-setting-backup.component';

/** タブ */
export type DisplayTypeViewModel =
  | 'apiKey'
  | 'tag'
  | 'alertNotification'
  | 'backupMail'
  | 'notificationMail';

@Component({
  selector: 'app-tenant-setting',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonToggleModule,
    MatCardModule,
    TenantSettingApikeyComponent,
    TenantSettingTagComponent,
    TenantSettingNotificationComponent,
    TenantSettingBackupComponent,
  ],
  templateUrl: './tenant-setting.component.html',
  styleUrl: './tenant-setting.component.scss',
})
export class TenantSettingComponent implements OnInit {
  /** テナントID */
  public tenantId: string = '';

  /** Webhook */
  public webhook: WebhookViewModel = {
    apiKeyName: '',
    webhookUrl: '',
    keyHeaderName: '',
    apiKeyValue: '',
    hasApikey: false,
  };
  /** Apikey */
  public apikey: TenantSettingApikeyViewModel = {
    hasApikey: false,
    clientId: '',
    clientSecretValue: '',
  };

  /** テナント通知 */
  public tenantAlert: TenantAlertViewModel = {
    notifyKeywords: {},
  };

  /** 編集前のテナント通知 */
  public originalTenantAlert: TenantAlertViewModel = {
    notifyKeywords: {},
  };
  /** タブ */
  public selectedDisplayType: DisplayTypeViewModel = 'apiKey';
  /** タグ一覧 */
  public tagList: TagViewModel[] = [];

  /** バックアップメールアドレス */
  public backupEmailStr: string = '';
  /** 掲示板メールアドレス */
  public notificationEmailStr: string = '';

  @Output() public selection = new SelectionModel<TagViewModel>(true, []);

  constructor(
    private tenantSettingService: TenantSettingService,
    private dialog: MatDialog,
    private changeDetector: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.initTagList();
    this.initTenantAttr();
    this.initTenantAlert();
  }

  /**
   * タグ追加/編集ダイアログを表示する
   * @param tag タグ
   */
  public async openTagEditDialog(tag?: TagViewModel) {
    const options: DialogOption = {
      updating: false,
      tagList: this.tagList,
    };

    if (tag) {
      options.updating = true;
      options.target = {
        tagName: tag.tagName,
        tagId: tag.tagId,
        color: tag.color,
      };
    }

    const editedTag: TagViewModel = await firstValueFrom(
      this.dialog
        .open(TenantSettingTagEditDialogComponent, {
          width: '700px',
          data: options,
        })
        .afterClosed(),
    );
    if (!editedTag) {
      return;
    }

    if (options.updating) {
      // タグ更新処理
      if (
        tag &&
        tag.tagName === editedTag.tagName &&
        tag.color === editedTag.color
      ) {
        // タグ名が同じ場合はAPIを呼び出さずにダイアログを閉じる
        return;
      }
      const updatedTagRequest: TagUpdateRequest = {
        tagName: editedTag.tagName,
        tagId: editedTag.tagId,
        color: editedTag.color || undefined,
      };
      this.tenantSettingService.update(updatedTagRequest).subscribe({
        next: () => {
          // タグ一覧を再取得する
          this.initTagList();
          this.selection.clear();
        },
        error: (error) => {
          let message = '';
          if (error.response.error.detail.type == 'AlreadyExistsWebException') {
            message = `タグ名が重複しているため、更新に失敗しました。\n別の名前でもう一度お試しください。`;
          } else {
            message = `タグの変更に失敗しました。\nしばらくしてからもう一度お試しください。`;
          }
          const alertDialogOptions: AlertDialogOption = {
            type: 'error',
            message: message,
          };
          this.dialog.open(AlertDialogComponent, {
            width: '600px',
            data: alertDialogOptions,
          });
        },
      });
    } else {
      // タグ追加処理
      const addedTagRequest: TagAddRequest = {
        tagName: editedTag.tagName,
        color: editedTag.color || '#fef08a',
      };
      this.tenantSettingService.add(addedTagRequest).subscribe({
        next: () => {
          // アカウント一覧を再取得する
          this.initTagList();
          this.selection.clear();
        },
        error: (error) => {
          let message = '';
          if (error.response.error.detail.type == 'AlreadyExistsWebException') {
            message = `タグ名が重複しているため、追加に失敗しました。\n別の名前でもう一度お試しください。`;
          } else {
            message = `タグの追加に失敗しました。\nしばらくしてからもう一度お試しください。`;
          }
          const alertDialogOptions: AlertDialogOption = {
            type: 'error',
            message: message,
          };
          this.dialog.open(AlertDialogComponent, {
            width: '600px',
            data: alertDialogOptions,
          });
        },
      });
    }
  }

  /**
   * タグを削除する
   * @param tagList タグ一覧
   */
  public async deleteTag(tagList: TagViewModel[]) {
    const options: AlertDialogOption = {
      type: 'error',
      title: 'Confirm',
      message: `選択したタグ ${tagList.length} 件を削除してよろしいですか？`,
      operation: '削除',
    };

    const result: boolean = await firstValueFrom(
      this.dialog
        .open(AlertDialogComponent, {
          width: '600px',
          data: options,
        })
        .afterClosed(),
    );

    if (!result) {
      return;
    }

    this.tenantSettingService.delete(tagList).subscribe({
      next: () => {
        // タグ一覧を再取得する
        this.initTagList();
        this.selection.clear();
      },
      error: (error) => {
        let message = '';
        if (error.response.status === 409) {
          message = `タグ削除に失敗しました。\nユーザーもしくはデバイスにアサインされているタグが存在します。\n${error.response.error.detail.parameters.tag_list}`;
        } else {
          message = `タグ削除に失敗しました。\nしばらくしてからもう一度お試しください。`;
        }
        const alertDialogOptions: AlertDialogOption = {
          type: 'error',
          message: message,
        };
        this.dialog.open(AlertDialogComponent, {
          width: '600px',
          data: alertDialogOptions,
        });
      },
    });
  }

  private initTagList() {
    this.tenantSettingService.findAll().subscribe({
      next: (tagList) => {
        this.tagList = tagList.map((tag) => ({
          tagName: tag.tagName,
          tagId: tag.tagId,
          notifyEmail: tag.notifyEmail,
          color: tag.color,
        }));
      },
    });
  }

  private initTenantAttr(): void {
    // Webhook情報をサーバから取得して設定する
    this.tenantSettingService.findTenantAttr().subscribe({
      next: (tenantAttr) => {
        this.tenantId = tenantAttr.tenantId;
        this.webhook = {
          apiKeyName: tenantAttr.apiKeyName ?? '',
          webhookUrl: tenantAttr.webhookUrl ?? '',
          keyHeaderName: tenantAttr.keyHeaderName ?? '',
          apiKeyValue: tenantAttr.apiKeyValue ?? '',
          hasApikey: tenantAttr.hasAppclientId ?? false,
        };
        this.backupEmailStr = tenantAttr.matpNotifyEmail ?? '';
        this.notificationEmailStr = tenantAttr.notificationNotifyEmail ?? '';
      },
    });
  }

  public initTenantAlert(): void {
    this.tenantSettingService.findTenantAlert().subscribe({
      next: (tenantAlert) => {
        this.tenantAlert = {
          notifyKeywords: tenantAlert.notifyKeywords || {},
        };
        this.originalTenantAlert = this.tenantAlert;
        this.changeDetector.markForCheck();
      },
    });
  }

  public openApikeyEditDialog(): void {
    this.tenantSettingService.addApiKey().subscribe({
      next: (apiKey) => {
        this.apikey = {
          hasApikey: true,
          clientId: apiKey.clientId,
          clientSecretValue: apiKey.clientSecret,
        };

        const options: TenantSettingApikeyViewModel = this.apikey;
        this.dialog.open(TenantSettingApikeyEditDialogComponent, {
          width: '800px',
          data: options,
        });
      },
    });
  }

  public async deleteApikey(): Promise<void> {
    const options: AlertDialogOption = {
      type: 'error',
      title: 'Confirm',
      message: `API Keyを削除してよろしいですか？`,
      operation: '削除',
    };
    const result: boolean = await firstValueFrom(
      this.dialog
        .open(AlertDialogComponent, {
          width: '600px',
          data: options,
        })
        .afterClosed(),
    );

    if (!result) {
      return;
    }

    // Apikey削除処理
    this.tenantSettingService.deleteApiKey().subscribe({
      next: () => {
        this.apikey.hasApikey = false;
      },
    });
  }

  public async openWebhookEditDialog(webhook: WebhookViewModel): Promise<void> {
    const options: AlertDialogOption = {
      type: 'warn',
      title: 'Confirm',
      message: `Webhookを更新してよろしいですか？`,
      operation: '更新',
    };
    const result: boolean = await firstValueFrom(
      this.dialog
        .open(AlertDialogComponent, {
          width: '600px',
          data: options,
        })
        .afterClosed(),
    );

    if (!result) {
      // キャンセル時に元の値にリセットさせるため、変更検知させる
      this.webhook = {
        ...this.webhook,
      };

      return;
    }

    // 更新処理
    const request: TenantAttrRequest = {
      apiKeyName: webhook.apiKeyName,
      webhookUrl: webhook.webhookUrl,
      keyHeaderName: webhook.keyHeaderName,
      apiKeyValue: webhook.apiKeyValue,
      matpNotifyEmail: this.backupEmailStr,
      notificationNotifyEmail: this.notificationEmailStr,
    };
    this.tenantSettingService.updateTenantAttr(request).subscribe({
      next: () => {
        this.initTenantAttr();
      },
      error: () => {
        const alertDialogOptions: AlertDialogOption = {
          type: 'error',
          message: `Webhook更新に失敗しました。\nしばらくしてからもう一度お試しください。`,
        };
        this.dialog.open(AlertDialogComponent, {
          width: '600px',
          data: alertDialogOptions,
        });
      },
    });
  }

  public async openWebhookDeleteDialog(
    webhook: WebhookViewModel,
  ): Promise<void> {
    const options: AlertDialogOption = {
      type: 'error',
      title: 'Confirm',
      message: `Webhookを削除してよろしいですか？`,
      operation: '削除',
    };
    const result: boolean = await firstValueFrom(
      this.dialog
        .open(AlertDialogComponent, {
          width: '600px',
          data: options,
        })
        .afterClosed(),
    );

    if (!result) {
      // キャンセル時に元の値にリセットさせるため、変更検知させる
      this.webhook = {
        ...this.webhook,
      };

      return;
    }

    // 削除処理
    const request: TenantAttrRequest = {
      apiKeyName: webhook.apiKeyName,
      webhookUrl: webhook.webhookUrl,
      keyHeaderName: webhook.keyHeaderName,
      apiKeyValue: webhook.apiKeyValue,
      matpNotifyEmail: this.backupEmailStr,
      notificationNotifyEmail: this.notificationEmailStr,
    };
    this.tenantSettingService.deleteWebhook(request).subscribe({
      next: () => {
        this.initTenantAttr();
      },
      error: () => {
        const alertDialogOptions: AlertDialogOption = {
          type: 'error',
          message: `Webhook削除に失敗しました。\nしばらくしてからもう一度お試しください。`,
        };
        this.dialog.open(AlertDialogComponent, {
          width: '600px',
          data: alertDialogOptions,
        });
      },
    });
  }

  public async updateTenantAlert(
    tenantAlert: TenantAlertViewModel,
    tagList: TagViewModel[],
  ): Promise<void> {
    const options: AlertDialogOption = {
      type: 'warn',
      title: 'Confirm',
      message: `メールアラート通知を更新してよろしいですか？`,
      operation: '更新',
    };

    const result: boolean = await firstValueFrom(
      this.dialog
        .open(AlertDialogComponent, {
          width: '600px',
          data: options,
        })
        .afterClosed(),
    );

    if (!result) {
      // キャンセル時に元の値にリセットさせるため、変更検知させる
      this.tenantAlert = {
        ...this.tenantAlert,
      };
      this.tagList = [...this.tagList];
      return;
    }

    let updateTenantAlertSuccess = false;
    let updateNotifyEmailsSuccess = false;

    try {
      // テナント通知の更新
      await firstValueFrom(
        this.tenantSettingService.updateTenantAlert(tenantAlert),
      );
      updateTenantAlertSuccess = true; // 成功フラグを立てる

      const request: TagUpdateListRequest = {
        tagList: tagList,
      };
      // タグの更新
      await firstValueFrom(
        this.tenantSettingService.updateNotifyEmails(request),
      );

      updateNotifyEmailsSuccess = true; // 成功フラグを立てる

      // 両方の更新が成功した場合
      this.initTenantAlert();
      this.initTagList();
    } catch (error) {
      // エラーハンドリング
      let message = '';
      if (updateTenantAlertSuccess == false) {
        message = `メールアラート通知の変更に失敗しました。\nしばらくしてからもう一度お試しください。`;
      } else if (updateNotifyEmailsSuccess == false) {
        message = `タグの変更に失敗しました。\nしばらくしてからもう一度お試しください。`;
      } else {
        message = `変更に失敗しました。\nしばらくしてからもう一度お試しください。`;
      }

      const alertDialogOptions: AlertDialogOption = {
        type: 'error',
        message: message,
      };
      this.dialog.open(AlertDialogComponent, {
        width: '600px',
        data: alertDialogOptions,
      });
    }
  }

  public async updateBackupEmail(backupEmailStr: string): Promise<void> {
    const options: AlertDialogOption = {
      type: 'warn',
      title: 'Confirm',
      message: `バックアップメールアドレスを更新してよろしいですか？`,
      operation: '更新',
    };
    // 確認ダイアログを表示
    this.dialog
      .open(AlertDialogComponent, {
        width: '600px',
        data: options,
      })
      .afterClosed()
      .subscribe(async (result: boolean) => {
        if (!result) {
          return;
        }
        // テナント属性のリクエストを作成
        const request: TenantAttrRequest = {
          apiKeyName: this.webhook.apiKeyName,
          webhookUrl: this.webhook.webhookUrl,
          keyHeaderName: this.webhook.keyHeaderName,
          apiKeyValue: this.webhook.apiKeyValue,
          matpNotifyEmail: backupEmailStr,
          notificationNotifyEmail: this.notificationEmailStr,
        };
        // バックアップメールを保存するAPI呼び出し
        this.tenantSettingService.updateTenantAttr(request).subscribe({
          next: () => {
            this.tenantSettingService.findTenantAttr().subscribe({
              next: (tenantAttr) => {
                this.backupEmailStr = tenantAttr.matpNotifyEmail ?? '';
              },
            });
          },
          error: () => {
            const alertDialogOptions: AlertDialogOption = {
              type: 'error',
              message: `バックアップメールの変更に失敗しました。\nしばらくしてからもう一度お試しください。`,
            };
            this.dialog.open(AlertDialogComponent, {
              width: '600px',
              data: alertDialogOptions,
            });
          },
        });
      });
  }

  public async updateNotificationEmail(
    notificationEmailStr: string,
  ): Promise<void> {
    const options: AlertDialogOption = {
      type: 'warn',
      title: 'Confirm',
      message: '掲示板メールアドレスを更新してよろしいですか？',
      operation: '更新',
    };
    const confirmed = await firstValueFrom(
      this.dialog
        .open(AlertDialogComponent, { width: '600px', data: options })
        .afterClosed(),
    );
    if (!confirmed) {
      return;
    }

    // テナント属性のリクエストを作成
    const request: TenantAttrRequest = {
      apiKeyName: this.webhook.apiKeyName,
      webhookUrl: this.webhook.webhookUrl,
      keyHeaderName: this.webhook.keyHeaderName,
      apiKeyValue: this.webhook.apiKeyValue,
      matpNotifyEmail: this.backupEmailStr,
      notificationNotifyEmail: notificationEmailStr,
    };
    // 掲示板メールアドレスを変更するAPI呼び出し
    this.tenantSettingService.updateTenantAttr(request).subscribe({
      next: (tenantAttr) => {
        this.notificationEmailStr = tenantAttr.notificationNotifyEmail ?? '';
      },
      error: () => {
        const alertDialogOptions: AlertDialogOption = {
          type: 'error',
          message:
            '掲示板メールの変更に失敗しました。\nしばらくしてからもう一度お試しください。',
        };
        this.dialog.open(AlertDialogComponent, {
          width: '600px',
          data: alertDialogOptions,
        });
      },
    });
  }
}
